import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Layout from '../components/Layout';
import api from '../services/api';

const ProfilePage = () => {
  const { user } = useSelector((state) => state.auth);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    // Clear messages when user starts typing
    setError('');
    setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      setError('All fields are required');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New password and confirm password do not match');
      return;
    }

    if (passwordData.current_password === passwordData.new_password) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      setSuccess('Password changed successfully!');
      // Clear form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid" style={{ maxWidth: '800px' }}>
        <h1 className="fs-3 mb-4">My Profile</h1>

        {/* User Information Card */}
        <div className="card mb-4">
          <div className="card-body">
            <h2 className="fs-6 fw-semibold border-bottom pb-3 mb-3">Profile Information</h2>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Name:</span>
                <span className="small text-break">{user?.name}</span>
              </div>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Email:</span>
                <span className="small text-break">{user?.email}</span>
              </div>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Employee Code:</span>
                <span className="small text-break">{user?.employee_code}</span>
              </div>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Role:</span>
                <span className="badge bg-primary text-capitalize">{user?.role}</span>
              </div>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Department(s):</span>
                <span className="small text-break">
                  {user?.departments ? user.departments.join(', ') : user?.department}
                </span>
              </div>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Phone:</span>
                <span className="small text-break">{user?.phone}</span>
              </div>
              <div className="d-flex flex-column flex-sm-row align-items-sm-center bg-light rounded-2 p-2 px-3 gap-1 gap-sm-0">
                <span className="fw-semibold text-muted small flex-shrink-0" style={{ minWidth: '160px' }}>Status:</span>
                <span className={`badge ${user?.is_active ? 'bg-success' : 'bg-danger'}`}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="card">
          <div className="card-body">
            <h2 className="fs-6 fw-semibold border-bottom pb-3 mb-3">Change Password</h2>

            {error && (
              <div className="alert alert-danger py-2 small">{error}</div>
            )}

            {success && (
              <div className="alert alert-success py-2 small">{success}</div>
            )}

            <form onSubmit={handleSubmit} style={{ maxWidth: '500px' }}>
              <div className="mb-3">
                <label className="form-label">Current Password *</label>
                <div className="input-group">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    name="current_password"
                    className="form-control"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter your current password"
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => togglePasswordVisibility('current')}
                    tabIndex="-1"
                  >
                    <i className={`ti ${showPasswords.current ? 'ti-eye-off' : 'ti-eye'}`} />
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">New Password *</label>
                <div className="input-group">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="new_password"
                    className="form-control"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password (min 6 characters)"
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => togglePasswordVisibility('new')}
                    tabIndex="-1"
                  >
                    <i className={`ti ${showPasswords.new ? 'ti-eye-off' : 'ti-eye'}`} />
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Confirm New Password *</label>
                <div className="input-group">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirm_password"
                    className="form-control"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    placeholder="Re-enter new password"
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => togglePasswordVisibility('confirm')}
                    tabIndex="-1"
                  >
                    <i className={`ti ${showPasswords.confirm ? 'ti-eye-off' : 'ti-eye'}`} />
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
